import {
  CUSTOMER_READ_ROLES,
  hasAnyOpsRole,
  type CustomerManagerCustomerSummary,
  type ListCustomerManagerCustomersResponse,
  type ListOpsDirectoryUsersResponse,
  type OpsDirectoryUser,
} from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSession } from '../hooks/useSession.tsx';
import {
  customerIdentityLabel,
  opsUserIdentityLabel,
} from '../lib/identityLabel.ts';
import {
  listAllQuotaCustomers,
  QUOTA_CUSTOMERS_KEY,
} from './customerOwnership.ts';
import { opsApiRequest } from './client.ts';

export const OPS_USER_DIRECTORY_KEY = ['directory', 'ops-users'] as const;

const DIRECTORY_STALE_TIME_MS = 5 * 60_000;

const EMPTY_OPS_USER_INDEX: ReadonlyMap<string, OpsDirectoryUser> = new Map();
const EMPTY_CUSTOMER_INDEX: CustomerDirectoryIndex = {
  byOrgId: new Map(),
  byAccountId: new Map(),
};

const opsUserIndexCache = new WeakMap<
  ListOpsDirectoryUsersResponse,
  ReadonlyMap<string, OpsDirectoryUser>
>();

export function indexOpsUsers(
  data: ListOpsDirectoryUsersResponse,
): ReadonlyMap<string, OpsDirectoryUser> {
  const cached = opsUserIndexCache.get(data);
  if (cached !== undefined) {
    return cached;
  }
  const byId = new Map<string, OpsDirectoryUser>();
  for (const user of data.users) {
    byId.set(user.id, user);
  }
  opsUserIndexCache.set(data, byId);
  return byId;
}

export interface CustomerDirectoryIndex {
  readonly byOrgId: ReadonlyMap<string, CustomerManagerCustomerSummary>;
  readonly byAccountId: ReadonlyMap<string, CustomerManagerCustomerSummary>;
}

const customerIndexCache = new WeakMap<
  ListCustomerManagerCustomersResponse,
  CustomerDirectoryIndex
>();

export function indexCustomers(
  data: ListCustomerManagerCustomersResponse,
): CustomerDirectoryIndex {
  const cached = customerIndexCache.get(data);
  if (cached !== undefined) {
    return cached;
  }
  const byOrgId = new Map<string, CustomerManagerCustomerSummary>();
  const byAccountId = new Map<string, CustomerManagerCustomerSummary>();
  for (const customer of data.customers) {
    byOrgId.set(customer.org_id, customer);
    byAccountId.set(customer.account_id, customer);
  }
  const index: CustomerDirectoryIndex = { byOrgId, byAccountId };
  customerIndexCache.set(data, index);
  return index;
}

export async function listOpsUserDirectory(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOpsDirectoryUsersResponse> {
  return opsApiRequest<ListOpsDirectoryUsersResponse>(
    '/v1/ops/directory/ops-users',
    { signal },
    fetchImpl,
  );
}

export function useOpsUserDirectory(): { readonly byId: ReadonlyMap<string, OpsDirectoryUser> } {
  const { fetchImpl, status } = useSession();
  const query = useQuery({
    queryKey: OPS_USER_DIRECTORY_KEY,
    queryFn: ({ signal }) => listOpsUserDirectory(fetchImpl, signal),
    staleTime: DIRECTORY_STALE_TIME_MS,
    enabled: status === 'authenticated',
    retry: false,
    select: indexOpsUsers,
  });
  return { byId: query.data ?? EMPTY_OPS_USER_INDEX };
}

export function useCustomerDirectory(): {
  readonly byOrgId: ReadonlyMap<string, CustomerManagerCustomerSummary>;
  readonly byAccountId: ReadonlyMap<string, CustomerManagerCustomerSummary>;
} {
  const { fetchImpl, status, user } = useSession();
  const canReadCustomers =
    status === 'authenticated' &&
    user !== null &&
    hasAnyOpsRole(user.roles, CUSTOMER_READ_ROLES);
  const query = useQuery({
    queryKey: QUOTA_CUSTOMERS_KEY,
    queryFn: ({ signal }) => listAllQuotaCustomers(fetchImpl, signal),
    staleTime: DIRECTORY_STALE_TIME_MS,
    enabled: canReadCustomers,
    retry: false,
    select: indexCustomers,
  });
  return query.data ?? EMPTY_CUSTOMER_INDEX;
}

export function useOpsUserLabel(): (id: string | null | undefined) => string {
  const { byId } = useOpsUserDirectory();
  return useCallback(
    (id: string | null | undefined) => {
      if (id === null || id === undefined || id.length === 0) {
        return '';
      }
      return opsUserIdentityLabel(id, byId.get(id));
    },
    [byId],
  );
}

export function useCustomerLabel(): {
  readonly byOrg: (id: string | null | undefined) => string;
  readonly byAccount: (id: string | null | undefined) => string;
} {
  const { byOrgId, byAccountId } = useCustomerDirectory();
  const byOrg = useCallback(
    (id: string | null | undefined) => {
      if (id === null || id === undefined || id.length === 0) {
        return '';
      }
      return customerIdentityLabel(id, byOrgId.get(id));
    },
    [byOrgId],
  );
  const byAccount = useCallback(
    (id: string | null | undefined) => {
      if (id === null || id === undefined || id.length === 0) {
        return '';
      }
      return customerIdentityLabel(id, byAccountId.get(id));
    },
    [byAccountId],
  );
  return { byOrg, byAccount };
}
